import { lazy, Suspense } from 'react';
import { Route, Switch } from 'react-router';

import { IonRouterOutlet } from '@ionic/react';

import { getAnimationBuilder, isNative } from '../helpers/device.helper';
import { useCapturePostHogPageView } from '../hooks/useCapturePostHogPageView';
import { useSetStatusBar } from '../hooks/useSetStatusBar';
import { useWebNotificationListener } from '../hooks/useWebNotificationListener';
import { AuthRoute } from './AuthRoute';
import { InitialRoute } from './InitialRoute';
import { SuspenseFallback } from './SuspenseFallback';

const AdvocateForgotPasswordPage = lazy(() => import('../pages/advocate/AdvocateForgotPassword.page'));
const AdvocateLoginPage = lazy(() => import('../pages/advocate/AdvocateLogin.page'));
const AdvocateResetPasswordPage = lazy(() => import('../pages/advocate/AdvocateResetPassword.page'));
const AdvocateTabs = lazy(() => import('./AdvocateTabs.router'));
const GuestTabs = lazy(() => import('./GuestTabs.router'));
const SettingsPage = lazy(() => import('../pages/settings/Settings.page'));
const AboutPage = lazy(() => import('../pages/about/About.page'));
const TSLoadingPage = lazy(() => import('../pages/loading/ts-loading.page'));
const TSOnboardingPage = lazy(() => import('../pages/onboarding/ts-onboarding.page'));
const PreferencesPage = lazy(() => import('../pages/preferences/Preferences.page'));
const DocumentsPage = lazy(() => import('../pages/documents/Documents.page'));

const guestPaths = ['/guest/chat', '/guest/services'];
if (isNative()) guestPaths.push('/guest/alert');

const advocatePaths = ['/advocate/chat', '/advocate/services', '/advocate/resources', '/advocate/account'];
if (isNative()) advocatePaths.push('/advocate/alert');

export const MiddlewareRouter = () => {
  useWebNotificationListener();
  useCapturePostHogPageView();
  useSetStatusBar();

  return (
    <Switch>
      <Route exact path={guestPaths}>
        <Suspense fallback={<SuspenseFallback />}>
          <GuestTabs />
        </Suspense>
      </Route>
      <AuthRoute exact path={advocatePaths}>
        <Suspense fallback={<SuspenseFallback />}>
          <AdvocateTabs />
        </Suspense>
      </AuthRoute>
      <IonRouterOutlet animation={getAnimationBuilder}>
        <Route exact path="/ts-onboarding">
          <Suspense fallback={<SuspenseFallback />}>
            <TSOnboardingPage />
          </Suspense>
        </Route>
        <Route exact path="/ts-loading">
          <Suspense fallback={<SuspenseFallback />}>
            <TSLoadingPage />
          </Suspense>
        </Route>
        <Route exact path="/advocate-login">
          <Suspense fallback={<SuspenseFallback />}>
            <AdvocateLoginPage />
          </Suspense>
        </Route>
        <Route exact path="/advocate-forgot-password">
          <Suspense fallback={<SuspenseFallback />}>
            <AdvocateForgotPasswordPage />
          </Suspense>
        </Route>
        <Route exact path="/advocate-reset-password/:token">
          <Suspense fallback={<SuspenseFallback />}>
            <AdvocateResetPasswordPage />
          </Suspense>
        </Route>
        <Route exact path="/settings">
          <Suspense fallback={<SuspenseFallback />}>
            <SettingsPage />
          </Suspense>
        </Route>
        <Route exact path="/preferences">
          <Suspense fallback={<SuspenseFallback />}>
            <PreferencesPage />
          </Suspense>
        </Route>
        <Route exact path="/documents">
          <Suspense fallback={<SuspenseFallback />}>
            <DocumentsPage />
          </Suspense>
        </Route>
        <Route exact path="/about">
          <Suspense fallback={<SuspenseFallback />}>
            <AboutPage />
          </Suspense>
        </Route>
        <InitialRoute />
      </IonRouterOutlet>
    </Switch>
  );
};
